/**
 * The two pieces of arithmetic that make a torpedo playable: where to throw
 * it, and whether one is about to arrive.
 *
 * Both live here rather than in the browser because they are pure geometry and
 * the bots will want them next. Nothing in this file reads a clock, draws
 * anything, or knows which side is looking — it takes numbers and returns
 * numbers, and refuses to answer when there is no answer.
 *
 * ### Lead
 *
 * A fish runs at a fixed speed on a fixed bearing, so hitting a ship that is
 * itself moving is the constant-bearing intercept: find `t` where the target's
 * straight line and a circle of radius `speed * t` about the tube meet. That
 * is one quadratic. It has no positive root when the target is opening faster
 * than the fish can swim, and in that case the honest answer is nothing at
 * all — a marker drawn on an unreachable ship would teach the player a lie
 * about their own weapon, which is worse than no marker.
 *
 * The solution is taken from the ship's centre rather than from a tube. A bank
 * sits six metres off the centreline and the shortest shot anybody takes is
 * measured in hundreds of metres, so the offset is inside the width of the fan
 * and carrying it would only make the marker jitter as the hull rolls.
 *
 * ### Threat
 *
 * A warning has to answer "will this one hit me, and in how long", and the
 * counter-play — turn bow- or stern-on so the fan runs past the length of the
 * ship instead of across its beam — only works if the prediction assumes both
 * ships hold their course. So it is solved in the relative frame: the hull
 * stands still, the fish carries the difference of the two velocities, and the
 * answer changes the moment the helm does. The hull is the same capsule the
 * torpedo actually detonates on (`hullCapsule`), so the warning and the hit
 * agree about what counts as the ship.
 *
 * Fuel is ignored. A fish is only visible to the other side inside
 * `TORPEDO_SPOT_RANGE` (1200 m), and twenty seconds at 30 m/s is 600 m — a
 * torpedo that could run out before it arrives is one nobody can see yet.
 */
import { hullCapsule } from './simulation';
import type { ShipSpec } from './ship-spec';

/** How far ahead the warning looks. Long enough to turn a destroyer, short enough to mean it. */
export const TORPEDO_WARNING_SECONDS = 20;

export interface TorpedoLead {
  /** Where the target will be when the fish gets there. */
  x: number;
  z: number;
  /** Seconds of run to the intercept. */
  time: number;
  /** World bearing from the launch point to the intercept, in the game's yaw convention. */
  bearing: number;
}

/**
 * Where to aim a fish of `speed` launched from (`ox`, `oz`) at a target at
 * (`tx`, `tz`) making (`vx`, `vz`), or `null` when it cannot be caught inside
 * `maxRange`.
 */
export function torpedoLead(
  ox: number,
  oz: number,
  tx: number,
  tz: number,
  vx: number,
  vz: number,
  speed: number,
  maxRange = Infinity,
): TorpedoLead | null {
  if (!(speed > 0)) return null;
  const dx = tx - ox;
  const dz = tz - oz;

  // |d + v t| = speed t, squared.
  const a = vx * vx + vz * vz - speed * speed;
  const b = 2 * (dx * vx + dz * vz);
  const c = dx * dx + dz * dz;

  let time: number;
  if (Math.abs(a) < 1e-9) {
    // The target matches the fish's speed exactly: the quadratic degenerates.
    // There is one solution, and only while the range is closing.
    if (b >= -1e-9) return null;
    time = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc < 0) return null;
    const root = Math.sqrt(disc);
    const t1 = (-b - root) / (2 * a);
    const t2 = (-b + root) / (2 * a);
    // Smallest strictly positive root: the first time the circle reaches the line.
    const lo = Math.min(t1, t2);
    const hi = Math.max(t1, t2);
    if (lo > 1e-6) time = lo;
    else if (hi > 1e-6) time = hi;
    else return null;
  }
  if (!Number.isFinite(time) || time <= 0) return null;
  if (time * speed > maxRange) return null;

  const x = tx + vx * time;
  const z = tz + vz * time;
  return { x, z, time, bearing: Math.atan2(x - ox, z - oz) };
}

export interface IncomingFish {
  id: number;
  x: number;
  z: number;
  /** The bearing it is running on. Fixed for its whole life. */
  yaw: number;
  speed: number;
  /** Launched by the viewer's own side, and so not a threat to them. */
  mine: boolean;
}

export interface TorpedoThreat {
  id: number;
  /** Seconds until it reaches the hull. Zero means it is inside it now. */
  seconds: number;
  /** World bearing from the ship to the fish. */
  bearing: number;
  /**
   * The same bearing read off the bow: 0 is dead ahead, ±π is astern. Positive
   * is to port, because the hull frame's lateral axis is (`torpedoHullHit`
   * uses the same one to decide which side a warhead struck).
   */
  relative: number;
  /** Metres to the fish right now. */
  distance: number;
}

interface Moving {
  x: number;
  z: number;
  yaw: number;
  speed: number;
}

/**
 * Whether `fish` reaches `ship`'s hull inside `horizon` seconds, both holding
 * course. `null` when it passes clear, is already past, or is running away.
 */
export function torpedoThreatTo(
  fish: Moving,
  ship: Moving,
  spec: ShipSpec,
  horizon = TORPEDO_WARNING_SECONDS,
): Omit<TorpedoThreat, 'id'> | null {
  const capsule = hullCapsule(spec);
  const ox = fish.x - ship.x;
  const oz = fish.z - ship.z;
  const vx = Math.sin(fish.yaw) * fish.speed - Math.sin(ship.yaw) * ship.speed;
  const vz = Math.cos(fish.yaw) * fish.speed - Math.cos(ship.yaw) * ship.speed;

  // Into the hull's frame: `f` along the keel, `r` across it.
  const s = Math.sin(ship.yaw);
  const c = Math.cos(ship.yaw);
  const pf = ox * s + oz * c;
  const pr = ox * c - oz * s;
  const vf = vx * s + vz * c;
  const vr = vx * c - vz * s;

  const t = capsuleEntry(pf, pr, vf, vr, capsule.half, capsule.radius);
  if (t === null || t > horizon) return null;
  return {
    seconds: t,
    bearing: Math.atan2(ox, oz),
    relative: Math.atan2(pr, pf),
    distance: Math.hypot(ox, oz),
  };
}

/**
 * Every enemy fish that will reach the hull, soonest first. `mine` is dropped
 * before any geometry runs: a torpedo cannot hit the side that launched it
 * (`torpedoArmedAgainst`), so warning about one would be noise.
 */
export function incomingTorpedoes(
  fish: readonly IncomingFish[],
  ship: Moving,
  spec: ShipSpec,
  horizon = TORPEDO_WARNING_SECONDS,
): TorpedoThreat[] {
  const out: TorpedoThreat[] = [];
  for (const f of fish) {
    if (f.mine) continue;
    const threat = torpedoThreatTo(f, ship, spec, horizon);
    if (threat) out.push({ id: f.id, ...threat });
  }
  out.sort((a, b) => a.seconds - b.seconds);
  return out;
}

/**
 * The first time a point at (`pf`, `pr`) moving (`vf`, `vr`) is inside the
 * stadium of half-length `half` and radius `radius` about the origin, or
 * `null` if it never is. Zero when it starts inside.
 *
 * The stadium is the rectangle plus its two end discs, and entry into a union
 * is the earliest entry into any part of it, so the three are solved
 * separately and the smallest kept.
 */
function capsuleEntry(
  pf: number,
  pr: number,
  vf: number,
  vr: number,
  half: number,
  radius: number,
): number | null {
  let best: number | null = null;
  const keep = (t: number | null): void => {
    if (t === null) return;
    if (best === null || t < best) best = t;
  };
  keep(slabEntry(pf, pr, vf, vr, half, radius));
  keep(discEntry(pf - half, pr, vf, vr, radius));
  keep(discEntry(pf + half, pr, vf, vr, radius));
  return best;
}

/** Entry into the box |f| ≤ half, |r| ≤ radius. */
function slabEntry(
  pf: number,
  pr: number,
  vf: number,
  vr: number,
  half: number,
  radius: number,
): number | null {
  const f = slab(pf, vf, half);
  if (!f) return null;
  const r = slab(pr, vr, radius);
  if (!r) return null;
  const lo = Math.max(f[0], r[0]);
  const hi = Math.min(f[1], r[1]);
  if (lo > hi || hi < 0) return null;
  return Math.max(0, lo);
}

/** The interval of `t` for which `p + v t` stays within ±`bound`, or null. */
function slab(p: number, v: number, bound: number): [number, number] | null {
  if (Math.abs(v) < 1e-9) return Math.abs(p) <= bound ? [-Infinity, Infinity] : null;
  const a = (-bound - p) / v;
  const b = (bound - p) / v;
  return a < b ? [a, b] : [b, a];
}

/** Entry into a disc of `radius` centred on the origin of the given offsets. */
function discEntry(pf: number, pr: number, vf: number, vr: number, radius: number): number | null {
  const a = vf * vf + vr * vr;
  const c = pf * pf + pr * pr - radius * radius;
  if (c <= 0) return 0;
  if (a < 1e-12) return null;
  const b = 2 * (pf * vf + pr * vr);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const root = Math.sqrt(disc);
  const t = (-b - root) / (2 * a);
  const exit = (-b + root) / (2 * a);
  if (exit < 0) return null;
  return Math.max(0, t);
}
